import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NotificationService } from '@app/core/services/notification.service';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { DateRange } from '@app/shared/models/date-range';
import { QuickRange, QuickRangeMapping, quickRangeToDate } from '@app/shared/models/quick-range';
import { dateLikeToNgbDate, DateUtils } from '@app/utils/date';
import { loggerFor } from '@app/utils/logger';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { StringMap } from '@app/utils/string-map';
import { BootstrapViewport, Viewport } from '@app/utils/viewport';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'portal-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [ngValueAccessor(DateRangePicker)],
  standalone: false,
})
export class DateRangePicker extends ReactiveComponent implements OnInit {
  @ViewChild('popover', { static: false })
  public popover: TippyDirective;

  @ViewChild('resizeSensor', { static: true })
  public resizeSensor: ElementRef;

  @Input()
  public disabled: boolean = false;

  @Input()
  public from: Date;

  @Input()
  public label: string;

  @Input()
  public maxDate: Date;

  @Input()
  public minDate: Date;

  @Input()
  public ngClass: string | string[] | Set<string> | StringMap<any>;

  @Input()
  public range: QuickRange;

  @Input()
  public showPredefinedRanges: boolean = true;

  @Input()
  public to: Date;

  @Output()
  public dateRangeChanged = new EventEmitter<DateRange>();

  /**
   * Make the QuickRange accessible to the template
   */
  public QuickRange = QuickRange;

  /**
   * Indicates if at least one predefined range can be selected
   */
  public anyRangeAvailable: boolean = true;

  /**
   * Contains for all quick ranges if they are available for the user to select (based on maxDate and minDate input properties)
   */
  public availableRanges: StringMap<boolean> = {};

  /**
   * Selected from date
   */
  public fromDate: NgbDate;

  /**
   * Currently hovered date
   */
  public hoveredDate: NgbDate;

  /**
   * The value the input displays
   */
  public inputValue: string;

  /**
   * If the range picker is currently open (i.e. months are shown and user can select a date range)
   */
  public isOpen: boolean = false;

  /**
   * The last date that can be displayed or selected
   */
  public ngbMaxDate: NgbDate;

  /**
   * The earliest date that can be displayed or selected.
   */
  public ngbMinDate: NgbDate;

  public selectedRange?: QuickRange = undefined;

  /**
   * The date to open calendar with
   */
  public startDate: NgbDate;

  /**
   * Selected to date
   */
  public toDate: NgbDate;

  /**
   * How many months should be displayed (based on the bootstrap viewport)
   */
  public visibleMonths: number = 1;

  /**
   * Localized date pipe
   */
  private datePipe: DatePipe;

  /**
   * The format of the input value.
   */
  private inputFormat: 'shortDate' | 'mediumDate' = 'mediumDate';

  /**
   * Logger for this component
   */
  private logger = loggerFor('DateRangePicker');

  private viewport: Viewport;

  @HostBinding('class')
  get isOpenClassName(): string {
    return this.isOpen ? 'date-range-picker--is-open' : '';
  }

  constructor(
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    // Set initial values from the given inputs
    this.fromDate = dateLikeToNgbDate(this.from);
    this.toDate = dateLikeToNgbDate(this.to);

    if (this.maxDate !== undefined) {
      this.ngbMaxDate = dateLikeToNgbDate(this.maxDate);
    }

    if (this.minDate !== undefined) {
      this.ngbMinDate = dateLikeToNgbDate(this.minDate);
    }

    this.calculateAvailableRanges();
    this.setByRange(this.range);

    // Whenever the language of the user changes we need to recreate the date pipe
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe((locale) => {
      this.createDatePipe(locale.lang);
      this.updateInputValue();
    });

    this.createDatePipe(this.translateService.currentLang);
    this.handleViewportChanges();

    this.emitDateRange();
    this.viewportSizeChanged();
    this.updateInputValue();
  }

  /**
   * Close the date range picker
   */
  public close() {
    this.popover.hide();
  }

  /**
   * Calculates the date to start the calendar with
   */
  public calculateStartDate() {
    if (this.visibleMonths > 1) {
      const endMonthShown = DateUtils.lastDayOfMonth(
        DateUtils.addMonths(this.fromDate, this.visibleMonths - 1)
      );

      const endOfMonthAvailable = DateUtils.lastDayOfMonth(this.maxDate);

      if (DateUtils.isAfter(endMonthShown, endOfMonthAvailable)) {
        this.startDate = DateUtils.parseToNgbDate(
          DateUtils.subMonths(endOfMonthAvailable, this.visibleMonths - 1)
        );
      } else {
        this.startDate = this.fromDate;
      }

      if (this.startDate.before(this.ngbMinDate)) {
        this.startDate = this.ngbMinDate;
      }
    } else {
      this.startDate = this.fromDate;
    }

    this.setOpenState(true);
  }

  /**
   * Returns the formatted date based on the current locale
   * @param date
   */
  public formatDate(date: NgbDate) {
    const jsDate = DateUtils.parse(date);

    let format: string = this.inputFormat;

    if (this.inputFormat === 'shortDate') {
      if (this.translateService.currentLang === 'de') {
        format = 'dd.MM.';
      } else {
        format = 'MMM d';
      }
    }

    return this.datePipe.transform(jsDate, format);
  }

  /**
   * Checks if the given date is currently hovered and if so if it's not inside the currently selected range
   * @param date
   */
  public isHovered(date: NgbDate): boolean {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  /**
   * Checks if the given date is inside the currently selected date range (i.e. > fromDate and < toDate)
   * @param date
   */
  public isInside(date: NgbDate): boolean {
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  /**
   * Checks if the given date is part of the currently selected range (i.e. >= fromDate and <= toDate) or if it is hovered
   * @param date
   */
  public isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) || date.equals(this.toDate) || this.isInside(date) || this.isHovered(date)
    );
  }

  /**
   * Called when the user selected a new date
   * @param date
   */
  public onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate) {
      // If the newly selected date is before the previously chosen one then let's use this as the new from date
      if (date.before(this.fromDate)) {
        this.fromDate = date;
      } else {
        this.toDate = date;
        this.selectedRange = undefined;

        this.emitDateRange();
        this.updateInputValue();

        // let's close the popup since both dates are now set
        this.close();
      }
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  /**
   * Open the datepicker
   */
  public open() {
    this.popover.show();
  }

  /**
   * Select the given range and emit the newly selected date range
   * @param range
   */
  public selectRange(range: QuickRange) {
    const successful = this.setByRange(range);

    if (successful) {
      this.selectedRange = range;

      this.emitDateRange();
      this.updateInputValue();

      this.close();
    } else {
      this.notificationService.showDanger(`Couldn't find range mapping for QuickRange.${range}`);
    }
  }

  /**
   * Called when the reactive disabled state changes
   * @param isDisabled
   */
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Set the open state for the component
   * @param open
   */
  public setOpenState(open: boolean): void {
    this.isOpen = open;
  }

  /**
   * Called when the reactive value changes
   * @param dateRange
   */
  public writeValue(dateRange: DateRange): void {
    if (dateRange === undefined) {
      this.fromDate = undefined;
      this.toDate = undefined;
    } else {
      this.fromDate = dateLikeToNgbDate(dateRange.from);
      this.toDate = dateLikeToNgbDate(dateRange.to);
    }

    this.updateInputValue();
  }

  /**
   * Calculate for all quick ranges if they are available for the user to select (based on the given maxDate and minDate properties)
   */
  private calculateAvailableRanges(): void {
    const quickRangeKeys = Object.keys(QuickRange);

    this.availableRanges = {};
    this.anyRangeAvailable = false;

    if (this.minDate === undefined) {
      for (const key of quickRangeKeys) {
        this.availableRanges[key] = true;
      }

      this.anyRangeAvailable = true;
    } else {
      const referenceDate = DateUtils.today();
      const allowedDateRange = { from: this.minDate, to: this.maxDate || referenceDate };

      for (const key of quickRangeKeys) {
        const quickRange = QuickRangeMapping[key](referenceDate);

        this.availableRanges[key] = DateUtils.overlaps(quickRange, allowedDateRange);

        if (this.availableRanges[key] === true) {
          this.anyRangeAvailable = true;
        }
      }
    }
  }

  /**
   * Create the date pipe with the given locale
   * @param locale
   */
  private createDatePipe(locale: string) {
    this.datePipe = new DatePipe(locale);
  }

  /**
   * Emit the currently selected date range
   */
  private emitDateRange() {
    const dateRange = {
      from: new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day),
      to: new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day),
    };

    this.onChange(dateRange);
    this.dateRangeChanged.emit(dateRange);

    this.logger.debug('Emitted date range', dateRange);
  }

  /**
   * Attach events to the viewport and handle changes
   */
  private handleViewportChanges() {
    // Create a new viewport that handles the input date format
    this.viewport = new Viewport(
      {
        shortDate: [0, 210],
        mediumDate: [211, Infinity],
      },
      this.resizeSensor.nativeElement
    );

    this.viewport.size$.pipe(untilDestroyed(this)).subscribe((size: 'shortDate' | 'mediumDate') => {
      this.inputFormat = size;

      this.updateInputValue();
    });

    // Whenever the viewport size changes we refresh the how many months should be displayed
    BootstrapViewport.size$.pipe(untilDestroyed(this)).subscribe(() => {
      this.viewportSizeChanged();
    });
  }

  /**
   * Recalculate the visible months based on the current viewport
   */
  private viewportSizeChanged() {
    this.visibleMonths = BootstrapViewport.isDesktop() ? 3 : 1;

    this.updateInputValue();
  }

  /**
   * Calculates the from and to date for the given QuickRange
   * @param range
   * @return true if the QuickRange exists and from and to were successfully updated
   */
  private setByRange(range: QuickRange): boolean {
    let returnValue = false;

    let { from, to } = quickRangeToDate(range) ?? {};

    if (from !== undefined && to !== undefined) {
      this.selectedRange = range;

      if (DateUtils.isBefore(from, this.minDate)) {
        from = this.minDate;
      }

      if (DateUtils.isAfter(to, this.maxDate)) {
        to = this.maxDate;
      }

      this.fromDate = DateUtils.parseToNgbDate(from);
      this.toDate = DateUtils.parseToNgbDate(to);

      returnValue = true;

      this.logger.debug('Selected range', range);
    }

    return returnValue;
  }

  /**
   * Update the input value based on the currently selected fromDate and toDate
   */
  private updateInputValue() {
    let value = '';

    if (this.selectedRange !== undefined) {
      value = this.translateService.instant(`common.range.${this.selectedRange}`);
    } else if (this.fromDate && this.toDate) {
      const datesDiffer = this.fromDate.after(this.toDate) || this.fromDate.before(this.toDate);

      if (datesDiffer) {
        value = `${this.formatDate(this.fromDate)} - ${this.formatDate(this.toDate)}`;
      } else {
        value = this.formatDate(this.fromDate);
      }
    } else if (this.fromDate) {
      value = this.formatDate(this.fromDate);
    }

    this.inputValue = value;
  }
}
